// ============================================================================
// WORKOUT DOMAIN TYPES
// Matches backend Pydantic schemas (calendar_planning/structured_workout/)
// and Django models (calendar_planning/models.py)
// ============================================================================

import { sportColors, sportColorMeta } from '../theme/BeoflowSportColors.generated'
import type { BlockType } from '../lib/notation/blockTypes'

export type { BlockType }

// ---------------------------------------------------------------------------
// Enums (string unions matching backend Pydantic enum values)
// ---------------------------------------------------------------------------

export type GeneralActivityType =
  | 'HYBRID'
  | 'AEROBIC'
  | 'RUNNING'
  | 'BIKING'
  | 'SKATING'
  | 'SWIMMING'
  | 'STRENGTH'
  | 'PADDLING'
  | 'OTHER'
  | 'MULTISPORT'
  | 'MINDFULNESS'

/**
 * Volume (duration) types — THE source of truth. Adding one is adding a row.
 *
 * Everything derives from this table: the `DurationType` union below, the grid
 * builder's dropdown, every display helper in workout-utils, and the text
 * editor's autocomplete vocabulary AND its parser. Before this existed there
 * were five hand-maintained lists and they had already drifted — `lap_button`
 * was in the grid but in neither the notation vocabulary nor the parser, so a
 * lap_button step written into text mode could not be read back.
 *
 * `selectable: false` means "renders, never offered": the type still decodes
 * and displays for workouts that already carry it, but no picker shows it.
 */
export const DURATION_TYPE_SPECS = [
  {
    type: 'time',
    label: 'Time',
    shortLabel: 'Time',
    unit: 'mm:ss',
    selectable: true,
    constraints: { min: 1, max: 86400, step: 1 },
    defaultTargets: ['heartrate_zone', 'power_zone'],
    // Where this type sits in the unit menu, per sport family. The menu is the
    // coach's whole vocabulary, so order is product design, not alphabetical.
    menuRank: { cardio: 0, strength: 1 },
    // `tokens` drives BOTH the unit menu and the parser: [token, factor, inMenu].
    // factor converts the typed number into the canonical unit workout_data
    // stores; inMenu=false is a parse-only alias ("1 lap" reads, menu says "laps").
    notation: { example: '30 min · 45 sec · 2 min 30 sec', tokens: [['min', 60, true], ['sec', 1, true]] },
  },
  {
    type: 'distance',
    label: 'Distance',
    shortLabel: 'Dist',
    unit: 'm',
    selectable: true,
    constraints: { min: 1, max: 100000, step: 1 },
    defaultTargets: ['pace_zone', 'heartrate_zone'],
    menuRank: { cardio: 1, strength: 2 },
    notation: { example: '400 m · 5 km', tokens: [['m', 1, true], ['km', 1000, true], ['mi', 1609.34, false]] },
  },
  {
    type: 'reps',
    label: 'Reps',
    shortLabel: 'Reps',
    unit: 'reps',
    selectable: true,
    constraints: { min: 1, max: 999, step: 1 },
    defaultTargets: ['weight_kg', 'rpe'],
    menuRank: { cardio: 5, strength: 0 },
    notation: { example: '8 reps', tokens: [['reps', 1, true], ['rep', 1, false]] },
  },
  {
    type: 'laps',
    label: 'Laps',
    shortLabel: 'Laps',
    unit: 'laps',
    selectable: true,
    constraints: { min: 1, max: 999, step: 1 },
    defaultTargets: ['pace_zone', 'heartrate_zone'],
    menuRank: { cardio: 2, strength: 5 },
    // A count of laps of a track, pool or loop. The lap's LENGTH is a property
    // of the workout, not the step — see `lap_length_meters`.
    notation: { example: '4 laps', tokens: [['laps', 1, true], ['lap', 1, false]] },
  },
  {
    type: 'calories',
    label: 'Calories',
    shortLabel: 'Cal',
    unit: 'cal',
    selectable: true,
    constraints: { min: 1, max: 9999, step: 1 },
    defaultTargets: ['heartrate_zone', 'power_zone'],
    menuRank: { cardio: 4, strength: 4 },
    notation: { example: '200 cal', tokens: [['cal', 1, true]] },
  },
  {
    type: 'kilojoules',
    label: 'Kilojoules',
    shortLabel: 'Energy',
    unit: 'kJ',
    selectable: true,
    constraints: { min: 1, max: 9999, step: 1 },
    defaultTargets: ['heartrate_zone', 'power_zone'],
    menuRank: { cardio: 3, strength: 3 },
    notation: { example: '500 kJ', tokens: [['kJ', 1, true]] },
  },
  {
    // Retired in favour of `laps`, which is what coaches actually meant by it.
    // Kept unselectable so stored workouts still render; the backend still
    // accepts the value, so this cannot be deleted webapp-side alone.
    type: 'lap_button',
    label: 'Until lap button',
    shortLabel: 'Lap button',
    unit: '',
    selectable: false,
    constraints: null,
    menuRank: { cardio: 99, strength: 99 },
    defaultTargets: ['open'],
    notation: null,
  },
] as const satisfies readonly {
  type: string
  label: string
  shortLabel: string
  unit: string
  selectable: boolean
  constraints: { min: number; max: number; step: number } | null
  menuRank: { cardio: number; strength: number }
  defaultTargets: readonly string[]
  notation: { example: string; tokens: readonly (readonly [string, number, boolean])[] } | null
}[]

export type DurationType = typeof DURATION_TYPE_SPECS[number]['type']

/** Lookup by wire value. Unknown values (a newer backend type an older webapp
 *  has not learned yet) resolve to undefined — callers fall back rather than throw. */
export const DURATION_SPEC: Partial<Record<string, typeof DURATION_TYPE_SPECS[number]>> =
  Object.fromEntries(DURATION_TYPE_SPECS.map((s) => [s.type, s]))

/** The types a coach may pick, in menu order. */
export const SELECTABLE_DURATION_TYPES = DURATION_TYPE_SPECS.filter((s) => s.selectable)

export type IntensityType =
  | 'warmup'
  | 'active'
  | 'tempo'
  | 'threshold'
  | 'vo2max'
  | 'anaerobic'
  | 'neuromuscular'
  | 'recovery'
  | 'rest'
  | 'cooldown'

export type TargetType =
  // Zone-based
  | 'heartrate_zone'
  | 'power_zone'
  | 'pace_zone'
  // Percentage-based
  | 'heartrate_percent_max'
  | 'heartrate_percent_threshold'
  | 'power_percent_ft'
  | 'pace_percent_ft'
  // Absolute
  | 'heartrate_bpm'
  | 'power_watts'
  | 'speed_mps'
  // Target lap time in seconds, tenths precision. Already a backend TargetType
  // (LAP_TIME_SECONDS); the webapp read it and threw it away.
  | 'lap_time_seconds'
  | 'cadence_rpm'
  // Strength
  | 'weight_kg'
  | 'bar_speed_mps'
  // Other
  | 'rpe'
  | 'open'

export type TargetSpecifier = 'equal' | 'above' | 'below' | 'range'

export type EquipmentType =
  | 'barbell'
  | 'dumbbell'
  | 'kettlebell'
  | 'machine'
  | 'banded'
  | 'bodyweight'
  | 'medball'
  | 'cardio'
  | 'plyometric'
  | 'mobility'
  | 'stretching'
  | 'yoga'

export type MuscleGroup =
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'abs'
  | 'obliques'
  | 'trapezius'
  | 'lower_back'
  | 'glutes'
  | 'quads'
  | 'hamstrings'
  | 'hip_flexors'
  | 'calves'
  | 'inner_thigh'
  | 'neck'

export type SharingStatus = 'private' | 'sharable' | 'public'

export type RestDurationType = 'time' | 'hr_less_than' | 'open'

// ---------------------------------------------------------------------------
// Core workout structure (Pydantic models)
// ---------------------------------------------------------------------------

export interface WorkoutTarget {
  type: TargetType
  value: number | null
  value_low: number | null
  value_high: number | null
  specifier: TargetSpecifier | null
}

export interface WorkoutStep {
  id: string
  name: string | null
  /** Coach's note on the step. The backend already stores and renders this as
   *  `Note: "..."` (structured_workout_utils.py); the webapp simply never
   *  declared it, so it was dropped on every edit. */
  description?: string | null
  intensity: IntensityType | null
  duration_type: DurationType | null
  duration_value: number | null
  duration_unit: string | null
  // Optional: a step may carry no intensity targets (e.g. an imported session
  // that only specifies exercise + duration). Consumers must treat missing
  // targets as an empty list — never assume an array. Defaults for metric/strain
  // math are applied in-memory at calculation time, not persisted.
  targets?: WorkoutTarget[]
  exercise_id: string | number
  rest_time: number | null
  rest_type: RestDurationType | null
  completed: boolean
  completion_notes: string | null
  equipment?: string | null
  thumbnail_url?: string | null
}

export interface WorkoutRepeatGroup {
  id: string
  name: string | null
  description?: string | null
  repeat_count: number
  steps: WorkoutStepElement[] | null
  superset_number: number | null
  /** What KIND of block this is: EMOM, AMRAP, circuit, drop set and so on.
   *  Orthogonal to `superset_number`, which only tags which sibling groups form
   *  one block. Null is a plain repeat, and null WITH a `superset_number` is a
   *  superset, which is every workout written before this field existed.
   *  Reading the kind off the exercise count is what this replaces. */
  block_type?: BlockType | null
  /** The block's seconds parameter: an EMOM's interval, an AMRAP or For Time
   *  cap. One field for both because they store the same thing; what differs is
   *  the meaning, which `block_type` already carries. */
  block_param_seconds?: number | null
  exercise_id: string | number
  repeats_visible: boolean
  /** Equipment type from the exercise library (not persisted by backend, populated on creation) */
  equipment?: string | null
  /** Thumbnail URL from the exercise library (not persisted by backend, populated on creation) */
  thumbnail_url?: string | null
  /** The custom exercise's own prose, enriched by the PUBLIC share payload
   *  (`sharing/_enrich_workout_exercise_media`). Named `exercise_description`,
   *  not `description`: the group's `description` field is the coach's note to
   *  the athlete and must never carry the exercise's prose. Present only in
   *  share payloads; pydantic drops it again on save (extra fields ignored). */
  exercise_description?: string | null
  /** The custom exercise's media array (ExerciseMediaSerializer shape), same
   *  enrichment as `exercise_description` — this is what lets a signed-out
   *  viewer open the exercise detail with no further request (BEO-3201). */
  media?: ExerciseMedia[]
}

/** A step element is either a single step or a repeat group */
export type WorkoutStepElement = WorkoutStep | WorkoutRepeatGroup

/** Type guard: true if the element is a WorkoutRepeatGroup (has steps array with children) */
export function isRepeatGroup(el: WorkoutStepElement): el is WorkoutRepeatGroup {
  // Both may have repeat_count from backend normalization.
  // A RepeatGroup has steps[] with children; a WorkoutStep has targets[].
  // Check: has repeat_count AND has steps that is an array with length > 0
  // OR has repeat_count and no targets array (it's a group wrapper, not a set)
  if (!('repeat_count' in el)) return false
  const r = el as unknown as Record<string, unknown>
  const hasStepsArray = Array.isArray(r.steps) && (r.steps as unknown[]).length > 0
  const hasTargets = Array.isArray(r.targets)
  // If it has targets, it's a WorkoutStep (set), not a group
  if (hasTargets) return false
  return hasStepsArray
}

/** True if any step (recursively, including repeat-group children) is rep-based
 *  (`duration_type === 'reps'`). Wahoo's plan format only understands endurance
 *  exit triggers (time/distance/kj/repeat) and has no rep concept, so a workout
 *  with any reps step cannot be pushed to Wahoo. */
/** Whether any step in the workout is measured in laps. Mirrors workoutHasReps:
 *  the grid, the text editor and the save gate all need the same answer. */
export function workoutHasLaps(workout: { steps?: WorkoutStepElement[] | null } | null | undefined): boolean {
  const scan = (els: WorkoutStepElement[]): boolean =>
    els.some((el) => (isRepeatGroup(el) ? scan(el.steps ?? []) : (el as WorkoutStep).duration_type === 'laps'))
  return scan(workout?.steps ?? [])
}

/** A laps workout with no lap length is incomplete, not merely unpolished, so
 *  this gates the save rather than warning after the fact. */
export function needsLapLength(
  workout: { steps?: WorkoutStepElement[] | null; lap_length_meters?: number | null } | null | undefined,
): boolean {
  return workoutHasLaps(workout) && !(workout?.lap_length_meters && workout.lap_length_meters > 0)
}

export function workoutHasReps(workout: { steps?: WorkoutStepElement[] | null } | null | undefined): boolean {
  const scan = (els: WorkoutStepElement[]): boolean =>
    els.some((el) => (isRepeatGroup(el) ? scan(el.steps ?? []) : (el as WorkoutStep).duration_type === 'reps'))
  return scan(workout?.steps ?? [])
}

export interface StructuredWorkoutData {
  name: string
  description?: string | null
  sport: string
  steps: WorkoutStepElement[]
  is_multisport: boolean
  estimated_time: number | null
  estimated_distance: number | null
  estimated_work: number | null
  difficulty_level: number | null
  workout_type: string | null
  created_by: string | null
  source: string | null
  external_id: string | null
  /** Length of one lap, in metres. Required as soon as any step is measured in
   *  laps: "6 laps" is not a prescription until a lap has a length, and every
   *  downstream estimate converts laps -> metres before doing anything else.
   *  Workout-level, not per-step — an athlete does not change tracks mid-session. */
  lap_length_meters?: number | null
}

// ---------------------------------------------------------------------------
// Exercise library
// ---------------------------------------------------------------------------

export interface Exercise {
  id: number | string
  name: string
  /**
   * The name with its equipment spelled out where the bare name is ambiguous:
   * "Push-Up (Bodyweight)". Absent or equal to `name` when nothing else shares
   * it. Derived server-side, never stored, and profile-aware, so an athlete's
   * own custom exercise shadowing a system name makes it contested for them
   * and nobody else.
   *
   * Show this wherever the name travels WITHOUT its equipment badge beside it:
   * an autocomplete row, notation text, anywhere a name is the whole
   * identifier. The library page stacks by `name` and shows a chip per variant,
   * so it does not need this; the picker renders a flat list and does.
   */
  qualified_name?: string
  type: 'system' | 'custom'
  category: string | null
  equipment: string | null
  primary_muscles: MuscleGroup[]
  description?: string
  exercise_complexity?: string
  laterality?: string
  displacement_meters?: number
  media_keys?: string[]
  media_urls?: string[]
  thumbnail_url?: string | null
  media?: ExerciseMedia[]
  is_active?: boolean
  /** Custom exercises only: the system exercise this is based on. */
  base_exercise_id?: number | string
  /** Custom exercises only: true when the requesting user owns it. */
  is_owner?: boolean
}

export interface ExerciseMedia {
  id: string
  file_id: string
  url: string
  media_type: 'image' | 'video'
  width: number | null
  height: number | null
  sort_order: number
  date_created: string
  thumbnail_url: string
  preview_url: string
  full_url: string
}
export interface CreateCustomExerciseInput {
  name: string
  base_exercise_id: number | string
  description?: string
  muscle_groups?: string[]
  equipment_override?: string | null
}

export interface ExerciseGroup {
  name: string
  variants: Exercise[]
  /** The "representative" variant — first active one, or first by ID */
  primary: Exercise
}

/** Merge variant exercise with a fallback: use variant's own fields where populated,
 *  fall back to the fallback exercise for any empty optional fields. */
export function mergeExerciseWithFallback(variant: Exercise, fallback: Exercise): Exercise {
  return {
    ...variant,
    description: variant.description || fallback.description,
    exercise_complexity: variant.exercise_complexity || fallback.exercise_complexity,
    laterality: variant.laterality || fallback.laterality,
    media_keys: variant.media_keys?.length ? variant.media_keys : fallback.media_keys,
    media_urls: variant.media_urls?.length ? variant.media_urls : fallback.media_urls,
    thumbnail_url: variant.thumbnail_url ?? fallback.thumbnail_url,
    primary_muscles: variant.primary_muscles?.length ? variant.primary_muscles : fallback.primary_muscles,
    category: variant.category ?? fallback.category,
    is_active: variant.is_active ?? fallback.is_active,
  }
}

// ---------------------------------------------------------------------------
// Django models (API serializer shapes)
// ---------------------------------------------------------------------------

export interface WorkoutFolder {
  id: number
  title: string
  description: string | null
  is_system: boolean
  system_key: string | null
  sort_order: number
}

export interface WorkoutTemplate {
  id: number
  name: string
  sport: string
  description: string
  sharing_status: SharingStatus
  workout_data: StructuredWorkoutData
  estimated_duration_seconds: number | null
  estimated_distance_meters: number | null
  estimated_tonnage_kg: number | null
  estimated_intensity_factor: number | null
  folder: number | null
  is_system: boolean
  system_key: string | null
  created_at: string
  updated_at: string
}

export interface StructuredWorkout {
  id: number
  name: string
  sport: string
  description: string
  workout_data: StructuredWorkoutData
  estimated_duration_seconds: number | null
  estimated_distance_meters: number | null
  estimated_tonnage_kg: number | null
  estimated_intensity_factor: number | null
  template: number | null
  created_at: string
  updated_at: string
}

export interface CompletedStructuredWorkout {
  id: number
  planned_workout: number | null
  reference_datetime: string
  started_at: string
  completed_at: string
  name: string
  sport: string
  description: string
  completed_workout_data: StructuredWorkoutData
  total_duration_seconds: number
  total_distance_meters: number | null
  total_tonnage_kg: number | null
  total_intensity_factor: number | null
  device_name: string | null
  app_version: string | null
  sync_id: string
  activity_max_heart_rate_bpm: number | null
  activity_avg_heart_rate_bpm: number | null
  mapped_activity_id: number | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const SPORT_DISPLAY: Record<GeneralActivityType, { name: string; color: string }> = {
  // Canonical sports — name + color generated from Drafted (Foundations / Sport Colors), via iOS ActivityTypeConfig.
  HYBRID:      { name: sportColorMeta.hybrid.name,   color: sportColors.hybrid },
  AEROBIC:     { name: sportColorMeta.aerobic.name,  color: sportColors.aerobic },
  RUNNING:     { name: sportColorMeta.running.name,  color: sportColors.running },
  BIKING:      { name: sportColorMeta.biking.name,   color: sportColors.biking },
  SKATING:     { name: sportColorMeta.skating.name,  color: sportColors.skating },
  SWIMMING:    { name: sportColorMeta.swimming.name, color: sportColors.swimming },
  STRENGTH:    { name: sportColorMeta.strength.name, color: sportColors.strength },
  PADDLING:    { name: sportColorMeta.paddling.name, color: sportColors.paddling },
  OTHER:       { name: sportColorMeta.other.name,    color: sportColors.other },
  // Webapp-only sports with no canonical GeneralActivityType / generated equivalent:
  MULTISPORT:  { name: 'Multisport',  color: '#FF8C00' },
  MINDFULNESS: { name: 'Mindfulness', color: '#B8A4D4' },
}

// User-selectable sports — aligned exactly with iOS `GeneralActivityType.allCases`
// (Models/GeneralActivityType.swift). MULTISPORT/MINDFULNESS stay in SPORT_DISPLAY
// for rendering legacy data but are NOT offered in the sport picker.
export const SELECTABLE_SPORTS: GeneralActivityType[] = [
  'HYBRID', 'AEROBIC', 'RUNNING', 'BIKING', 'SKATING', 'SWIMMING', 'STRENGTH', 'PADDLING', 'OTHER',
]

export function sportDisplayName(sport: GeneralActivityType): string {
  return SPORT_DISPLAY[sport]?.name ?? sport
}

export function sportColor(sport: GeneralActivityType): string {
  return SPORT_DISPLAY[sport]?.color ?? '#F8F6F4'
}

// Map an activity-type display label (e.g. the calendar ACTIVITY_TYPES values
// "Strength Training"/"Cardio Training", or a Garmin label like "STRENGTH
// TRAINING") to a canonical GeneralActivityType. The backend's StructuredWorkout
// validator only accepts these enum names, so this must run before a label is
// used as a workout `sport`. Unknown inputs fall back to 'OTHER'.
const SPORT_LABEL_ALIASES: Record<string, GeneralActivityType> = {
  RUNNING: 'RUNNING', RUN: 'RUNNING', WALK: 'RUNNING', WALKING: 'RUNNING', JOGGING: 'RUNNING',
  BIKING: 'BIKING', BIKE: 'BIKING', CYCLING: 'BIKING',
  SWIMMING: 'SWIMMING', SWIM: 'SWIMMING',
  STRENGTH: 'STRENGTH', 'STRENGTH TRAINING': 'STRENGTH', 'WEIGHT TRAINING': 'STRENGTH',
  WEIGHTLIFTING: 'STRENGTH', RESISTANCE: 'STRENGTH',
  AEROBIC: 'AEROBIC', CARDIO: 'AEROBIC', 'CARDIO TRAINING': 'AEROBIC', ENDURANCE: 'AEROBIC',
  SKATING: 'SKATING', 'ICE SKATING': 'SKATING',
  // Substring-free exact-label matches. ROWING belongs here: it is the same
  // general type (PADDLE SPORT, id 13) as of beoflow#2203. What does NOT follow
  // it across is the critical-power anchor — the backend keys that on the
  // modality token ('paddling' | 'rowing') resolved from the SPECIFIC activity,
  // not on the general type, so a rower is still priced against a rowing CP.
  'PADDLE SPORT': 'PADDLING', PADDLESPORT: 'PADDLING',
  PADDLING: 'PADDLING', PADDLE: 'PADDLING', KAYAKING: 'PADDLING', KAYAK: 'PADDLING',
  CANOEING: 'PADDLING', CANOE: 'PADDLING', SURFSKI: 'PADDLING', OUTRIGGER: 'PADDLING',
  'DRAGON BOAT': 'PADDLING', 'STAND UP PADDLEBOARDING': 'PADDLING',
  'STAND UP PADDLE': 'PADDLING', PADDLEBOARDING: 'PADDLING', PADDLEBOARD: 'PADDLING',
  ROWING: 'PADDLING', ROW: 'PADDLING', 'ROWING MACHINE': 'PADDLING',
  SCULLING: 'PADDLING', ERG: 'PADDLING',
  HYBRID: 'HYBRID', 'CROSS TRAINING': 'HYBRID', 'MIXED TRAINING': 'HYBRID',
  MULTISPORT: 'MULTISPORT',
  MINDFULNESS: 'MINDFULNESS', YOGA: 'MINDFULNESS',
  OTHER: 'OTHER',
}

export function activityTypeToSport(value: string | null | undefined): GeneralActivityType {
  if (!value) return 'OTHER'
  const key = value.replace(/_/g, ' ').trim().toUpperCase().replace(/\s+/g, ' ')
  return SPORT_LABEL_ALIASES[key] ?? 'OTHER'
}

export const INTENSITY_DISPLAY: Record<IntensityType, { label: string; color: string }> = {
  warmup:       { label: 'Warm Up',       color: '#FFD54F' },
  active:       { label: 'Active',        color: '#66BB6A' },
  tempo:        { label: 'Tempo',         color: '#FFA726' },
  threshold:    { label: 'Threshold',     color: '#EF5350' },
  vo2max:       { label: 'VO2 Max',       color: '#AB47BC' },
  anaerobic:    { label: 'Anaerobic',     color: '#E53935' },
  neuromuscular:{ label: 'Neuromuscular', color: '#D50000' },
  recovery:     { label: 'Recovery',      color: '#42A5F5' },
  rest:         { label: 'Rest',          color: '#BDBDBD' },
  cooldown:     { label: 'Cool Down',     color: '#29B6F6' },
}

export const EQUIPMENT_DISPLAY: Record<EquipmentType, { label: string; icon: string }> = {
  barbell:    { label: 'Barbell',     icon: 'fitness_center' },
  dumbbell:   { label: 'Dumbbell',    icon: 'fitness_center' },
  kettlebell: { label: 'Kettlebell',  icon: 'fitness_center' },
  machine:    { label: 'Machine',     icon: 'precision_manufacturing' },
  banded:     { label: 'Banded',      icon: 'fitness_center' },
  bodyweight: { label: 'Bodyweight',  icon: 'accessibility_new' },
  medball:    { label: 'Med Ball',    icon: 'sports_basketball' },
  cardio:     { label: 'Cardio',      icon: 'monitor_heart' },
  plyometric: { label: 'Plyometric',  icon: 'bolt' },
  mobility:   { label: 'Mobility',    icon: 'self_improvement' },
  stretching: { label: 'Stretching',  icon: 'self_improvement' },
  yoga:       { label: 'Yoga',        icon: 'self_improvement' },
}

export function equipmentIcon(type: EquipmentType): string {
  return EQUIPMENT_DISPLAY[type]?.icon ?? 'fitness_center'
}
